import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import { Shield, ArrowLeft, Check, Loader2, FileCheck, DollarSign, Globe, ShieldCheck, History, GitBranch } from "lucide-react";
import { Phase1Form } from "@/components/Phase1Form";
import { Phase2Audit } from "@/components/Phase2Audit";
import { Phase3Review } from "@/components/Phase3Review";
import { Phase4Export } from "@/components/Phase4Export";
import { AuditHistory } from "@/components/AuditHistory";
import { ArchitectureVersions } from "@/components/ArchitectureVersions";
import { TemplateSelector } from "@/components/TemplateSelector";
import { PresenceAvatars } from "@/components/PresenceAvatars";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  { num: 1, title: "Project Intake", desc: "Define requirements", icon: <FileCheck className="w-4 h-4" /> },
  { num: 2, title: "Triple-Audit", desc: "Automated audits", icon: <Shield className="w-4 h-4" /> },
  { num: 3, title: "Review & Fix", desc: "Address findings", icon: <ShieldCheck className="w-4 h-4" /> },
  { num: 4, title: "Certified Export", desc: "Download IaC", icon: <Globe className="w-4 h-4" /> }
];

export const WizardPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [showArchVersions, setShowArchVersions] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [targetTab, setTargetTab] = useState(null); // For navigating to specific tab in Phase1

  useEffect(() => {
    if (projectId) {
      loadProject(projectId);
    } else {
      // Show template selector for new projects
      setShowTemplateSelector(true);
    }
  }, [projectId]);

  const loadProject = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setProject(response.data);
      setCurrentStep(response.data.current_phase || 1);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("You don't have access to this project");
      } else {
        toast.error("Failed to load project");
      }
      navigate('/wizard');
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (name, templateId = null) => {
    try {
      setCreating(true);
      const payload = { name };
      if (templateId) {
        payload.template_id = templateId;
      }
      const response = await axios.post(`${API}/projects`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setProject(response.data);
      navigate(`/wizard/${response.data.id}`, { replace: true });
      toast.success(templateId ? "Project created from template!" : "Project created!");
      return response.data;
    } catch (error) {
      toast.error("Failed to create project");
      return null;
    } finally {
      setCreating(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setShowTemplateSelector(false);
  };

  const handleStartBlank = () => {
    setSelectedTemplate(null);
    setShowTemplateSelector(false);
  };

  const updateProject = (updatedProject) => {
    setProject(updatedProject);
  };

  const goToStep = (step) => {
    if (step <= (project?.current_phase || 1) + 1) {
      setCurrentStep(step);
    }
  };

  const handleArchRestore = () => {
    // Reload project after restoring an architecture version
    if (projectId) {
      loadProject(projectId);
    }
    setShowArchVersions(false);
  };

  // Handler for navigating to specific tab in Phase1 from remediation
  const handleNavigateToTab = useCallback((tab) => {
    setTargetTab(tab);
    setCurrentStep(1);
    toast.info(`Navigating to fix the issue...`);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00f0ff] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#09090b]/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 md:gap-4">
              <Button
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white text-xs md:text-sm"
                onClick={() => navigate('/')}
                data-testid="back-home-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1 md:mr-2" />
                <span className="hidden sm:inline">Home</span>
              </Button>
              <div className="w-px h-6 bg-white/10" />
              <div className="flex items-center gap-2 md:gap-3 group">
                <div className="relative">
                  <div className="absolute inset-0 bg-[#00f0ff]/20 blur-xl rounded-full scale-75 group-hover:scale-100 transition-transform" />
                  <img src="/zerozone-logo.png" alt="ZeroZone Logo" className="relative w-24 h-24 md:w-28 md:h-28 object-contain drop-shadow-[0_0_18px_rgba(59,130,246,0.5)]" />
                </div>
                <span className="font-semibold text-white text-sm md:text-base truncate max-w-[150px] md:max-w-none">
                  {project?.name || "New Architecture"}
                </span>
              </div>
              {project?.id && token && (
                <div className="hidden md:flex"><PresenceAvatars projectId={project.id} token={token} phase={currentStep} /></div>
              )}
            </div>
            
            {project && (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-zinc-400 hover:text-white hover:bg-white/5 text-xs md:text-sm"
                  onClick={() => setShowAuditHistory(true)}
                  data-testid="audit-history-btn"
                  title="View Audit History"
                >
                  <History className="w-4 h-4" />
                  <span className="hidden md:inline ml-2">Audits</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-zinc-400 hover:text-white hover:bg-white/5 text-xs md:text-sm"
                  onClick={() => setShowArchVersions(true)}
                  data-testid="arch-versions-btn"
                  title="Architecture Versions"
                >
                  <GitBranch className="w-4 h-4" />
                  <span className="hidden md:inline ml-2">Versions</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 text-zinc-300 hover:bg-white/5 text-xs md:text-sm"
                  onClick={() => navigate('/projects')}
                  data-testid="view-all-projects-btn"
                >
                  <span className="hidden sm:inline">All Projects</span>
                  <span className="sm:hidden">Projects</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Step Indicator */}
      <div className="border-b border-white/5 bg-[#09090b]/50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex items-center justify-between overflow-x-auto">
            {steps.map((step, index) => (
              <div 
                key={step.num}
                className="flex items-center flex-1 min-w-0"
              >
                <button
                  onClick={() => goToStep(step.num)}
                  disabled={!project || step.num > (project?.current_phase || 1) + 1}
                  className={`flex items-center gap-2 md:gap-4 group ${
                    step.num <= (project?.current_phase || 1) + 1 ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                  }`}
                  data-testid={`step-${step.num}-btn`}
                >
                  <div className={`
                    w-8 h-8 md:w-10 md:h-10 rounded-sm flex items-center justify-center font-bold text-xs md:text-sm transition-all
                    ${currentStep === step.num 
                      ? 'bg-[#00f0ff] text-black' 
                      : step.num < currentStep 
                        ? 'bg-[#00f0ff]/20 text-[#00f0ff] border border-[#00f0ff]/30'
                        : 'bg-zinc-900 text-zinc-500 border border-zinc-800'
                    }
                  `}>
                    {step.num < currentStep ? <Check className="w-3 h-3 md:w-4 md:h-4" /> : step.num}
                  </div>
                  <div className="text-left hidden md:block">
                    <div className={`text-sm font-semibold ${currentStep === step.num ? 'text-white' : 'text-zinc-500'}`}>
                      {step.title}
                    </div>
                    <div className="text-xs text-zinc-600">{step.desc}</div>
                  </div>
                </button>
                
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-px mx-2 md:mx-6 ${
                    step.num < currentStep ? 'bg-[#00f0ff]/30' : 'bg-zinc-800'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Template Selector for new projects */}
        {showTemplateSelector && !projectId && (
          <TemplateSelector
            onSelectTemplate={handleSelectTemplate}
            onStartBlank={handleStartBlank}
          />
        )}

        {/* Phase 1 Form */}
        {!showTemplateSelector && currentStep === 1 && (
          <Phase1Form 
            project={project}
            onCreateProject={(name) => createProject(name, selectedTemplate?.id)}
            onUpdateProject={updateProject}
            onNext={() => setCurrentStep(2)}
            creating={creating}
            selectedTemplate={selectedTemplate}
            targetTab={targetTab}
            onTabChanged={() => setTargetTab(null)}
          />
        )}
        
        {currentStep === 2 && project && (
          <Phase2Audit
            project={project}
            onUpdateProject={updateProject}
            onNext={() => setCurrentStep(3)}
            onBack={() => setCurrentStep(1)}
            onNavigateToTab={handleNavigateToTab}
          />
        )}
        
        {currentStep === 3 && project && (
          <Phase3Review
            project={project}
            onUpdateProject={updateProject}
            onNext={() => setCurrentStep(4)}
            onBack={() => setCurrentStep(2)}
          />
        )}
        
        {currentStep === 4 && project && (
          <Phase4Export
            project={project}
            onUpdateProject={updateProject}
            onBack={() => setCurrentStep(3)}
          />
        )}
      </main>

      {/* Audit History Dialog */}
      <Dialog open={showAuditHistory} onOpenChange={setShowAuditHistory}>
        <DialogContent className="bg-[#09090b] border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto">
          {project && (
            <AuditHistory 
              projectId={project.id} 
              onClose={() => setShowAuditHistory(false)} 
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Architecture Versions Dialog */}
      <Dialog open={showArchVersions} onOpenChange={setShowArchVersions}>
        <DialogContent className="bg-[#09090b] border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto">
          {project && (
            <ArchitectureVersions 
              projectId={project.id} 
              onClose={() => setShowArchVersions(false)}
              onRestore={handleArchRestore}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
